import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getCaptainAdvice, getCaptainRounds, getMyCaptains, getPlayer, setCaptain, type Round } from '../api';
import { useAuth } from '../auth';
import { toast } from '../toast';
import { haptic } from '../haptic';

const ROUND_LABELS: Record<Round, string> = {
  group_md1: 'Group MD1', group_md2: 'Group MD2', group_md3: 'Group MD3',
  r32: 'R32', r16: 'R16', qf: 'QF', sf: 'SF', final: 'Final',
};

export default function CaptainPicker() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: rounds } = useQuery({ queryKey: ['captain-rounds'], queryFn: getCaptainRounds, enabled: !!user });
  const { data: mine } = useQuery({ queryKey: ['captain-me', user], queryFn: getMyCaptains, enabled: !!user });
  const { data: meRoster } = useQuery({ queryKey: ['player', user], queryFn: () => getPlayer(user!), enabled: !!user });

  const mut = useMutation({
    mutationFn: ({ round, code }: { round: Round; code: string }) => setCaptain(round, code),
    onMutate: async ({ round, code }) => {
      await qc.cancelQueries({ queryKey: ['captain-me', user] });
      const prev = qc.getQueryData(['captain-me', user]);
      const now = new Date().toISOString();
      qc.setQueryData(['captain-me', user], (old: any) => {
        const others = (old?.picks ?? []).filter((p: any) => p.round !== round);
        return { picks: [...others, { round, user: user!, team_code: code, created_at: now, updated_at: now }] };
      });
      haptic.tap();
      return { prev };
    },
    onError: (e: Error, _v, ctx: any) => {
      if (ctx?.prev) qc.setQueryData(['captain-me', user], ctx.prev);
      toast.error(e.message);
    },
    onSuccess: () => { toast.success('Captain pick saved.'); haptic.success(); },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['captain-me'] });
      qc.invalidateQueries({ queryKey: ['captain-lb'] });
    },
  });

  const pickByRound = useMemo(() => new Map((mine?.picks ?? []).map((p) => [p.round, p.team_code])), [mine]);

  if (!user || !meRoster?.player) return null;
  const upcoming = (rounds?.rounds ?? []).filter((r) => new Date(r.lockAt).getTime() > Date.now()).slice(0, 4);
  if (upcoming.length === 0) return null;

  return (
    <section className="card p-4">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-xs uppercase tracking-widest text-dim">{'Captain pick (side-game)'}</h2>
      </div>
      <p className="text-[11px] text-dim mb-3">{'Side game only — does NOT affect main standings. Pick a captain per round; their points double on a separate leaderboard.'}</p>
      <div className="space-y-3">
        {upcoming.map((r) => <RoundRow key={r.round} round={r.round} lockAt={r.lockAt} pick={pickByRound.get(r.round)} teams={meRoster.player.teams} onPick={(code) => mut.mutate({ round: r.round, code })} pending={mut.isPending} />)}
      </div>
    </section>
  );
}

function RoundRow({ round, lockAt, pick, teams, onPick, pending }: {
  round: Round; lockAt: string; pick: string | undefined;
  teams: { code: string; name: string; flag: string }[];
  onPick: (code: string) => void; pending: boolean;
}) {
  const { data: advice } = useQuery({ queryKey: ['cap-advice', round], queryFn: () => getCaptainAdvice(round) });
  const bestCode = advice?.best?.code;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="text-xs font-semibold">{ROUND_LABELS[round]}</div>
        <div className="text-[10px] text-dim">Locks {new Date(lockAt).toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short', timeZone: 'Europe/Riga' })}</div>
      </div>
      {advice?.best && advice.best.score > 0 && (
        <div className="text-[10px] text-cyan-400 mb-1.5">
          AI suggests <span className="font-semibold">{advice.best.flag} {advice.best.code}</span> · {advice.best.score} pt AI proj
        </div>
      )}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
        {teams.map((tm) => {
          const isBest = tm.code === bestCode;
          const isPicked = pick === tm.code;
          return (
            <button
              key={tm.code}
              onClick={() => onPick(tm.code)}
              disabled={pending}
              className={`relative flex flex-col items-center gap-1 rounded-lg px-2 py-1.5 text-xs ${
                isPicked ? 'bg-pitch-500/20 ring-1 ring-pitch-500/40 text-pitch-400' : 'bg-ink-800 hover:bg-ink-700'
              } ${isBest && !isPicked ? 'ring-1 ring-cyan-500/40' : ''}`}
            >
              <span className="text-base leading-none">{tm.flag}</span>
              <span className="font-mono">{tm.code}</span>
              {isBest && <span className="absolute -top-1 -right-1 text-[8px] bg-cyan-500 text-ink-950 px-1 rounded">AI</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
